import {
  format_percentage_value,
  kw_to_watt,
  kw_to_watt_allow_empty,
  parse_percentage_value,
  watt_to_kw,
  watt_to_kw_allow_empty,
} from 'util/misc'
import { convertToLegacyCustomFields } from '../common'
import type { ComponentConfig } from '../types'

/** What the stored round-trip efficiency represents. Must match backend EfficiencyMeasurementType. */
export const EFFICIENCY_MEASUREMENT_TYPES = ['ac_ac', 'dc_dc'] as const
export type EfficiencyMeasurementTypeValue = typeof EFFICIENCY_MEASUREMENT_TYPES[number]

export function isEfficiencyMeasurementType(value: unknown): value is EfficiencyMeasurementTypeValue {
  return typeof value === 'string' && EFFICIENCY_MEASUREMENT_TYPES.includes(value as EfficiencyMeasurementTypeValue)
}

export const EFFICIENCY_MEASUREMENT_TYPE_CHOICES: { id: EfficiencyMeasurementTypeValue; name: string }[] = [
  { id: 'ac_ac', name: 'AC-to-AC (includes inverter)' },
  { id: 'dc_dc', name: 'DC-to-DC (battery only)' },
]

export const batteryComponentDataConfigs: ComponentConfig[] = [
  {
    source: 'code',
    label: 'Code',
    component: 'String',
  },
  {
    source: 'manufacturer_name',
    label: 'Manufacturer',
    component: 'AutocompleteInput',
    referenceInputParams: {
      label: 'Manufacturer',
      reference: 'manufacturers',
      allowEmpty: true,
      ignoreWarnings: ['associatedReference'],
    },
    extra: {
      freeSolo: true,
      fullWidth: true,
      optionValue: 'name',
    },
  },
  {
    source: 'kwh_optimal',
    label: 'Total Energy',
    component: 'Number',
    unit: 'kWh',
  },
  {
    source: 'end_of_life_capacity',
    label: 'End of Life Capacity',
    component: 'Number',
    unit: '0-1',
  },
  {
    source: 'power_max_continuous',
    label: 'Max Continuous Discharge Power',
    component: 'Number',
    unit: 'Watts',
    formatter: { parse: watt_to_kw, format: kw_to_watt },
  },
  {
    source: 'charge_power_max',
    label: 'Max Continuous Charge Power',
    component: 'Number',
    unit: 'Watts',
    formatter: { parse: watt_to_kw_allow_empty, format: kw_to_watt_allow_empty },
    extra: {
      helperText: 'If left blank, this will match the Max Continuous Discharge Power value.',
    },
  },
  {
    source: 'depth_of_discharge_factor',
    label: 'Depth of Discharge',
    component: 'Number',
    unit: '0-1',
  },
  {
    source: 'warranty_kwh_1_cycle_per_day',
    label: 'Total Warranted kWh',
    component: 'Number',
    unit: '1 cycle/day',
  },
  {
    source: 'efficiency_factor',
    label: 'Roundtrip Efficiency',
    component: 'Number',
    unit: '%',
    formatter: { parse: parse_percentage_value, format: (val) => format_percentage_value(val) },
  },
  {
    source: 'efficiency_measurement_type',
    label: 'Efficiency measurement type',
    component: 'Select',
    selectOptions: EFFICIENCY_MEASUREMENT_TYPE_CHOICES,
  },
  {
    source: 'voltage',
    label: 'Nominal Voltage',
    component: 'Number',
    unit: 'V',
  },
  {
    source: 'product_warranty',
    label: 'Product Warranty',
    component: 'Number',
    unit: 'years',
  },
  {
    source: 'skus',
    label: 'SKUs',
    component: 'String',
    unit: 'csv',
  },
]

const osSpecificBatteryDataConfigs: ComponentConfig[] = [
  {
    source: 'aging_factor',
    label: 'Aging Factor',
    component: 'Number',
    unit: '0-1',
  },
]

export const customBatteryDataConfigs: ComponentConfig[] = [
  ...batteryComponentDataConfigs,
  ...osSpecificBatteryDataConfigs,
]

// TODO: to clean up the usage of customBatteryDataFields and remove this method
export const customBatteryDataFields = convertToLegacyCustomFields(customBatteryDataConfigs)

export const batteryDataFields = customBatteryDataFields.filter((item) => item.source !== 'manufacturer_name')
